import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { dataAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Upload,
  Loader2,
  FileJson,
  Shield,
  AlertTriangle,
  CheckCircle,
  Truck,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DataBackup() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await dataAPI.export();
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dynamite_backup_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.stats.total_vehicles} vehicles, ${data.stats.total_services} services, ${data.stats.total_mods} mods`);
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      toast.error("Please select a JSON file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        
        // Validate structure
        if (!data.vehicles || !Array.isArray(data.vehicles)) {
          toast.error("Invalid backup file format");
          return;
        }
        
        setImportData(data);
        setShowImportConfirm(true);
      } catch (error) {
        toast.error("Failed to parse JSON file");
      }
    };
    reader.readAsText(file);
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImport = async () => {
    if (!importData) return;
    
    setImporting(true);
    setShowImportConfirm(false);
    
    try {
      const { data } = await dataAPI.import({
        vehicles: importData.vehicles || [],
        service_events: importData.service_events || [],
        mods: importData.mods || [],
      });
      
      toast.success(`Imported ${data.imported.vehicles} vehicles, ${data.imported.services} services, ${data.imported.mods} mods`);
      setImportData(null);
      
      // Redirect to garage
      setTimeout(() => navigate("/"), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import data");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Link
              to="/"
              data-testid="back-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <HeaderIcon />

              <h1 className="text-lg font-bold text-foreground">
                Data Backup
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* User Info */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <Truck className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Export Section */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="w-5 h-5 text-green-500" />
                Export Data
              </CardTitle>
              <CardDescription>
                Download all your vehicles, service records, and modifications as a JSON file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-zinc-900 rounded-sm p-4 border border-zinc-800">
                  <h4 className="text-sm font-medium text-foreground mb-2">What's included:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      All vehicles with full details
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      All service/maintenance records
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      All modifications and build info
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      Photos and attachments (as base64)
                    </li>
                  </ul>
                </div>
                
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  data-testid="export-btn"
                  className="w-full btn-tactical bg-green-600 text-white hover:bg-green-700"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export All Data
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Import Section */}
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                Import Data
              </CardTitle>
              <CardDescription>
                Restore your data from a previously exported JSON backup file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-amber-500/10 rounded-sm p-4 border border-amber-500/30">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-500">Important:</h4>
                      <p className="text-sm text-muted-foreground">
                        Importing will create new copies of all vehicles and records. 
                        Existing data will not be modified or deleted.
                      </p>
                    </div>
                  </div>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />
                
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  data-testid="import-btn"
                  variant="outline"
                  className="w-full btn-tactical border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileJson className="w-4 h-4 mr-2" />
                  )}
                  Select Backup File
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Import Confirmation Dialog */}
      <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Import</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>You are about to import:</p>
              <ul className="text-sm space-y-1 mt-2">
                <li>• {importData?.vehicles?.length || 0} vehicles</li>
                <li>• {importData?.service_events?.length || 0} service records</li>
                <li>• {importData?.mods?.length || 0} modifications</li>
              </ul>
              {importData?.export_date && (
                <p className="text-xs text-muted-foreground mt-2">
                  Backup from: {new Date(importData.export_date).toLocaleString()}
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleImport}
              data-testid="confirm-import-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              Import Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
